import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import "react-select/dist/react-select.css";
import { Tooltip } from "components/ui/tool-tip/tooltip";
import IoIosInformationCircleOutline from "react-icons/lib/io/ios-information";
import { selectors } from "reducers/patient-outreaches";
import { setFilters } from "actions/patient-outreaches";
import { productLineOptions } from "utils/enum";

const CALL_WINDOW_TOOLTIP =
  "The TCPA-derived hours when it's legal to call this patient — computed " +
  "from the patient's preferred timezone, falling back to branch / company " +
  "settings, then a Central-time default.";

const OUTREACH_STATES = [
  { value: "Unallocated", label: "Unallocated" },
  { value: "AllocatedToCoach", label: "Allocated to coach" },
  { value: "AllocatedToSynthpop", label: "Allocated to Synthpop" },
  { value: "ContactedToday", label: "Contacted today" },
  { value: "FailedAutomation", label: "Failed automation" }
];

const PRODUCT_LINE_OPTIONS = productLineOptions.map(p => ({
  value: p.value,
  label: p.text
}));

const FilterBar = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectors.getFilters);

  // Company/Team/Coach options come from /filter-options — only the values
  // present in today's S3_PatientOutreach. The whole-Phoenix entity lists are
  // hundreds of items long and most don't have any patients today; this is
  // narrower and more useful.
  const { companies = [], teams = [], coaches = [] } = useSelector(
    selectors.getFilterOptions
  );

  // Local staged state — typing into inputs does NOT spam the API. The user
  // commits via the Apply button or Enter key.
  const [pending, setPending] = useState(filters);

  // When `filters` changes externally (count tile click, Reset), re-sync
  // the staged copy so Apply correctly disables.
  useEffect(() => {
    setPending(filters);
  }, [filters]);

  const isDirty = JSON.stringify(pending) !== JSON.stringify(filters);

  const handleApply = e => {
    if (e) e.preventDefault();
    if (isDirty) dispatch(setFilters(pending));
  };

  const handleReset = () => {
    // Empty payload restores defaults — see reducer's filters handler.
    dispatch(setFilters({}));
  };

  const toggleOutreachState = state => {
    const current = new Set(pending.outreachStates || []);
    if (current.has(state)) current.delete(state);
    else current.add(state);
    setPending(prev => ({ ...prev, outreachStates: Array.from(current) }));
  };

  const patch = changes => setPending(prev => ({ ...prev, ...changes }));

  // react-select v1 multi mode: `value` is the array of selected option objects,
  // not just the values. Project pending GUIDs back to options for display.
  const selectedOptionsFor = (options, selectedValues) =>
    options.filter(o => (selectedValues || []).includes(o.value));

  const handleMultiSelectChange = key => selected =>
    patch({ [key]: (selected || []).map(o => o.value) });

  return (
    <form className="patient-outreach-filter-bar" onSubmit={handleApply}>
      <fieldset className="filter-group">
        <legend>Outreach state</legend>
        {OUTREACH_STATES.map(opt => (
          <label key={opt.value} className="filter-checkbox">
            <input
              type="checkbox"
              checked={(pending.outreachStates || []).includes(opt.value)}
              onChange={() => toggleOutreachState(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </fieldset>

      <div className="filter-group filter-group--inline">
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={!!pending.spanishOnly}
            onChange={e => patch({ spanishOnly: e.target.checked })}
          />
          Spanish-speaking only
        </label>
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={!!pending.inTcpaWindowNow}
            onChange={e => patch({ inTcpaWindowNow: e.target.checked })}
          />
          In call window now
          <Tooltip
            id="patient-outreach-call-window-filter-info"
            message={CALL_WINDOW_TOOLTIP}
          >
            <IoIosInformationCircleOutline
              size="0.9rem"
              style={{ marginLeft: 4, verticalAlign: "middle" }}
            />
          </Tooltip>
        </label>
      </div>

      <div className="filter-group filter-group--inline patient-outreach-filter-bar__multiselects">
        <label className="filter-field filter-field--grow">
          Company:
          <Select
            multi
            value={selectedOptionsFor(companies, pending.companyGuids)}
            onChange={handleMultiSelectChange("companyGuids")}
            options={companies}
            placeholder=""
          />
        </label>
        <label className="filter-field filter-field--grow">
          Team:
          <Select
            multi
            value={selectedOptionsFor(teams, pending.teamGuids)}
            onChange={handleMultiSelectChange("teamGuids")}
            options={teams}
            placeholder=""
          />
        </label>
        <label className="filter-field filter-field--grow">
          Allocated coach:
          <Select
            multi
            value={selectedOptionsFor(coaches, pending.allocatedCoachGuids)}
            onChange={handleMultiSelectChange("allocatedCoachGuids")}
            options={coaches}
            placeholder=""
          />
        </label>
        <label className="filter-field filter-field--grow">
          Product line:
          <Select
            multi
            value={selectedOptionsFor(PRODUCT_LINE_OPTIONS, pending.productLines)}
            onChange={handleMultiSelectChange("productLines")}
            options={PRODUCT_LINE_OPTIONS}
            placeholder=""
          />
        </label>
      </div>

      <div className="filter-group filter-group--inline">
        <label className="filter-field">
          Cycle count:{" "}
          <input
            type="number"
            min={0}
            value={pending.cycleCount ?? ""}
            onChange={e =>
              patch({
                cycleCount:
                  e.target.value === "" ? null : Number(e.target.value)
              })
            }
            placeholder="(any)"
          />
        </label>
        <label className="filter-field filter-field--grow">
          Patient lookup (name/phone/account#):{" "}
          <input
            type="text"
            value={pending.patientLookup || ""}
            onChange={e => patch({ patientLookup: e.target.value })}
            placeholder="Type then press Enter or Apply…"
          />
        </label>
      </div>

      <div className="filter-group filter-group--inline patient-outreach-filter-bar__actions">
        <button
          type="submit"
          className="patient-outreach-filter-bar__apply"
          disabled={!isDirty}
        >
          {isDirty ? "Apply filters" : "Filters applied"}
        </button>
        <button
          type="button"
          className="patient-outreach-filter-bar__reset"
          onClick={handleReset}
        >
          Reset
        </button>
      </div>
    </form>
  );
};

export default FilterBar;
